import React, { useMemo } from 'react';
import { AddToCalendar as AddToCalendarIcon, Direction } from '@withjoy/joykit/icons';
import type { EventScheduleFragment } from '@graphql/fragments/event.schedule.graphql.generated';
import type { ScheduleListTranslations } from '../ScheduleList.i18n';
import type { VariantStyles } from '../ScheduleList.types';
import type { ScheduleListTestIds } from '../ScheduleList.utils';
import { getCalendarButtonProps } from '../ScheduleList.utils';
import { useColorPaletteProvider } from '../../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { Box, ButtonV2, Flex, LinkV2, SpacingStack, TextV2 } from '@withjoy/joykit';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body3ToButtonsOverride,
  button2ToButtonsOverride,
  hed2ToCaptionOverride,
  label6ToCaptionOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { AddToCalendar } from '@shared/components/AddToCalendar';
import { getGoogleMapsSearchLink } from '../../../common/utils/getGoogleMapsSearchLink';

type SecondaryBlockProps = Readonly<{
  scheduleListVersion: 'v1' | 'v2';
  isMobile?: boolean;
  timeframe?: string;
  description?: string;
  item?: EventScheduleFragment;
  eventNameMarkup?: JSX.Element;
  virtualEventMarkup?: JSX.Element;
  shouldShowAddToCalendarButton?: boolean;
  translations?: ScheduleListTranslations;
  styleProps: VariantStyles['secondaryBlock'];
  testIds: ScheduleListTestIds['listItemTestIds'];
  onCalendarClicked?: (title: string, type: string) => void;
}>;

export const SecondaryBlock: React.FC<SecondaryBlockProps> = ({
  eventNameMarkup,
  styleProps,
  testIds,
  timeframe,
  translations,
  virtualEventMarkup,
  item,
  isMobile,
  description,
  onCalendarClicked,
  shouldShowAddToCalendarButton,
  scheduleListVersion,
}) => {
  const showDurationBlock = scheduleListVersion === 'v2' || !!(eventNameMarkup || timeframe);
  const { pageFontColor, accentColor } = useColorPaletteProvider();
  const textColorProps = pageFontColor ? { color: pageFontColor } : {};
  const calendarButtonStyles = useMemo(() => getCalendarButtonProps(accentColor), [accentColor]);
  const shouldShowAddToCalendar = isMobile && shouldShowAddToCalendarButton && timeframe && item?.startTime?.timestamp;
  const showGetDirectionsButton = isMobile && item?.location;

  if (!item) {
    return null;
  }

  return (
    <SpacingStack
      {...testIds.secondaryBlock.prop}
      {...styleProps.container}
    >
      {showDurationBlock && (
        <>
          <Box {...styleProps.durationBlockContainer}>
            {scheduleListVersion === 'v1' && eventNameMarkup}
            {timeframe && (
              <GuestSiteTypographyOverride override={scheduleListVersion === 'v1' ? label6ToCaptionOverride : hed2ToCaptionOverride}>
                <TextV2
                  {...testIds.timeframe.prop}
                  typographyVariant={scheduleListVersion === 'v1' ? 'label6' : 'hed2'}
                  {...styleProps.durationBlock}
                  {...textColorProps}
                  css={{ whiteSpace: 'pre-line' }}
                >
                  {timeframe}
                </TextV2>
              </GuestSiteTypographyOverride>
            )}
          </Box>
          {scheduleListVersion === 'v1' && (shouldShowAddToCalendar || showGetDirectionsButton) && (
            <Flex columnGap={2}>
              {shouldShowAddToCalendar && (
                <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
                  <AddToCalendar
                    start={item.startTime?.timestamp}
                    end={item.endTime?.timestamp}
                    title={item.name}
                    location={item.location ?? ''}
                    description={description}
                    handleClickAddToCalendar={onCalendarClicked}
                  >
                    <ButtonV2
                      {...testIds.addToCalendar.prop}
                      startIcon={() => <AddToCalendarIcon />}
                      variant="outline"
                      shape="rounded"
                      size={'lg'}
                      {...calendarButtonStyles}
                      {...styleProps.calendarBlock}
                    >
                      {translations?.addToCalendar}
                    </ButtonV2>
                  </AddToCalendar>
                </GuestSiteTypographyOverride>
              )}
              {showGetDirectionsButton && (
                <GuestSiteTypographyOverride override={body3ToButtonsOverride}>
                  <ButtonV2
                    {...testIds.getDirections.prop}
                    startIcon={() => <Direction />}
                    variant="outline"
                    shape="rounded"
                    size={'lg'}
                    {...calendarButtonStyles}
                    {...styleProps.calendarBlock}
                  >
                    <LinkV2
                      href={getGoogleMapsSearchLink(item.location ?? '')}
                      isExternal={true}
                      color={calendarButtonStyles.color}
                      textDecoration={'none'}
                      _hover={{ color: calendarButtonStyles.color }}
                      _visited={{ color: calendarButtonStyles.color }}
                    >
                      {translations?.getDirections}
                    </LinkV2>
                  </ButtonV2>
                </GuestSiteTypographyOverride>
              )}
            </Flex>
          )}
        </>
      )}
      {scheduleListVersion === 'v1' && virtualEventMarkup}
    </SpacingStack>
  );
};
